import { stepConfirmation } from './confirmation.js';
import {
  futureChangeTags,
  knowledgeCitations,
  knowledgeNumber,
  requirementsMet,
  type EngagementRequirementRef,
} from './future.js';
import { layoutMap } from './layout.js';
import { mapHandoffs } from './metrics.js';
import { cleanText, isStepType, type MapModel, type MapStep, type StepType } from './model.js';

/**
 * Questions about a map are answered by code (ADR-017 §5, rule 22). The LLM
 * picks one query from this fixed catalogue; `runMapQuery` computes the facts
 * from the version; a second call phrases a reply from the facts; and
 * `checkReplyNumbers` refuses a reply with any number the facts do not hold,
 * so the reply falls back to a code template.
 */
export const MAP_QUERIES = [
  'steps_by_type',
  'steps_in_lane',
  'steps_using_system',
  'unconfirmed_steps',
  'pain_points',
  'handoffs',
  'steps_citing',
] as const;

/** Questions about a future-state map (E13-F09 §2), answered by code like the rest. */
export const FUTURE_MAP_QUERIES = [
  'steps_with_vendor_evidence',
  'assumption_steps',
  'workaround_steps',
  'removed_steps',
  'steps_meeting_requirement',
  'steps_citing_item',
] as const;

export type MapQueryKind = (typeof MAP_QUERIES)[number] | (typeof FUTURE_MAP_QUERIES)[number];

export type MapQuery =
  | { query: 'steps_by_type'; step_type: StepType }
  | { query: 'steps_in_lane'; lane: string }
  | { query: 'steps_using_system'; system: string }
  | { query: 'unconfirmed_steps' }
  | { query: 'pain_points' }
  | { query: 'handoffs' }
  | { query: 'steps_citing'; evidence_number: string }
  | { query: 'steps_with_vendor_evidence' }
  | { query: 'assumption_steps' }
  | { query: 'workaround_steps' }
  | { query: 'removed_steps' }
  | { query: 'steps_meeting_requirement'; requirement_number: string }
  | { query: 'steps_citing_item'; item_number: string };

/** What a future-state map's questions are answered against. */
export interface MapQueryContext {
  /** The validated current state the future map was drawn against. */
  current?: MapModel;
  /** The engagement's active requirements, for `steps_meeting_requirement`. */
  requirements?: readonly EngagementRequirementRef[];
}

export interface MapQueryStep {
  key: string;
  name: string;
  type: StepType;
  lane: string;
  lane_name: string;
  system: string | null;
}

export interface MapQueryFacts {
  query: MapQuery;
  /** The number the question is about, counted by code. */
  count: number;
  /** The steps that answer it, in process order (left to right, then top to bottom). */
  steps: MapQueryStep[];
  /** For `handoffs`: each hand-off between people's lanes on the main path. */
  handoffs?: { from: MapQueryStep; to: MapQueryStep }[];
  /** For `pain_points`: each distinct pain point and the steps it is on. */
  pain_points?: { number: string | null; steps: string[] }[];
}

export type MapQueryResult =
  | { valid: true; facts: MapQueryFacts }
  | { valid: false; check: 'invalid_query' | 'unknown_lane'; message: string };

export function runMapQuery(
  model: MapModel,
  query: MapQuery,
  context: MapQueryContext = {},
): MapQueryResult {
  const layout = layoutMap(model);
  const position = (s: MapStep) => layout.steps.get(s.key)!;
  const inOrder = [...model.steps].sort(
    (a, b) => position(a).column - position(b).column || position(a).row - position(b).row,
  );
  const laneNames = new Map(model.lanes.map((l) => [l.key, l.name]));
  const fact = (s: MapStep): MapQueryStep => ({
    key: s.key,
    name: s.name,
    type: s.type,
    lane: s.lane,
    lane_name: laneNames.get(s.lane) ?? s.lane,
    system: s.system,
  });
  const answer = (steps: MapStep[], extra: Partial<MapQueryFacts> = {}): MapQueryResult => ({
    valid: true,
    facts: { query, count: steps.length, steps: steps.map(fact), ...extra },
  });

  switch (query?.query) {
    case 'steps_by_type':
      if (!isStepType(query.step_type)) return invalid('That is not a step type.');
      return answer(inOrder.filter((s) => s.type === query.step_type));
    case 'steps_in_lane': {
      const wanted = typeof query.lane === 'string' ? fold(query.lane) : '';
      const lane =
        model.lanes.find((l) => l.key === query.lane) ??
        model.lanes.find((l) => fold(l.name) === wanted);
      if (!lane)
        return {
          valid: false,
          check: 'unknown_lane',
          message: `There is no lane like that. The lanes are ${model.lanes.map((l) => l.name).join(', ')}.`,
        };
      return answer(inOrder.filter((s) => s.lane === lane.key));
    }
    case 'steps_using_system': {
      const wanted = typeof query.system === 'string' ? fold(query.system) : '';
      if (!wanted) return invalid('Name the system.');
      return answer(inOrder.filter((s) => s.system !== null && fold(s.system).includes(wanted)));
    }
    case 'unconfirmed_steps':
      return answer(inOrder.filter((s) => stepConfirmation(s).confirmation === 'unconfirmed'));
    case 'pain_points': {
      const onSteps = inOrder.filter((s) => s.pain_points.length > 0);
      const byId = new Map<string, { number: string | null; steps: string[] }>();
      for (const s of onSteps)
        for (const p of s.pain_points) {
          const entry = byId.get(p.pain_point_id) ?? { number: p.number, steps: [] };
          entry.steps.push(s.key);
          byId.set(p.pain_point_id, entry);
        }
      const pains = [...byId.values()];
      return {
        valid: true,
        facts: { query, count: pains.length, steps: onSteps.map(fact), pain_points: pains },
      };
    }
    case 'handoffs': {
      const steps = new Map(model.steps.map((s) => [s.key, s]));
      const handoffs = mapHandoffs(model).map((h) => ({
        from: fact(steps.get(h.from)!),
        to: fact(steps.get(h.to)!),
      }));
      return { valid: true, facts: { query, count: handoffs.length, steps: [], handoffs } };
    }
    case 'steps_citing': {
      const wanted =
        typeof query.evidence_number === 'string' ? codeKey(query.evidence_number) : '';
      if (!wanted) return invalid('Name the evidence item, for example E-010.');
      return answer(
        inOrder.filter((s) =>
          s.citations.some(
            (c) =>
              c.kind === 'discovery_evidence' &&
              c.evidence_number !== null &&
              codeKey(c.evidence_number) === wanted,
          ),
        ),
      );
    }
    case 'steps_with_vendor_evidence':
      return answer(inOrder.filter((s) => knowledgeCitations(s).length > 0));
    case 'assumption_steps':
      return answer(inOrder.filter((s) => s.is_assumption === true));
    case 'workaround_steps': {
      if (!context.current) return invalid(FUTURE_ONLY);
      const tags = futureChangeTags(model, context.current).steps;
      return answer(inOrder.filter((s) => tags[s.key]!.workaround));
    }
    case 'removed_steps': {
      if (!context.current) return invalid(FUTURE_ONLY);
      const removed = futureChangeTags(model, context.current).removed;
      return {
        valid: true,
        facts: {
          query,
          count: removed.length,
          steps: removed.map((r) => ({ ...r, system: null })),
        },
      };
    }
    case 'steps_meeting_requirement': {
      if (!context.requirements) return invalid(FUTURE_ONLY);
      const wanted =
        typeof query.requirement_number === 'string' ? codeKey(query.requirement_number) : '';
      if (!wanted) return invalid('Name the requirement, for example AP-006.');
      return answer(
        inOrder.filter((s) =>
          requirementsMet(s, context.requirements!).some((r) => codeKey(r.number) === wanted),
        ),
      );
    }
    case 'steps_citing_item': {
      const wanted = knowledgeNumber(query.item_number);
      if (!wanted) return invalid('Name the knowledge item, for example VK-00412.');
      return answer(
        inOrder.filter((s) => knowledgeCitations(s).some((c) => c.item_number === wanted)),
      );
    }
    default:
      return invalid('That is not one of the questions the map can answer.');
  }
}

const FUTURE_ONLY = 'That question is about a future-state map.';

/**
 * True only if every number in the reply appears in the facts (rule 22).
 * Digits, number words ("three") and codes ("E-010", "PP-04") count; ids are
 * ignored, and so are the "1." markers of a numbered list. A code must appear
 * in the facts as a code, and a plain number as a number, so "10 steps" is
 * not excused by "E-010".
 */
export function checkReplyNumbers(reply: string, facts: unknown): boolean {
  const said = numbersIn(reply);
  const known = { codes: new Set<string>(), values: new Set<number>() };
  collect(facts, known, 0);
  for (const code of said.codes) if (!known.codes.has(code)) return false;
  for (const value of said.values) if (!known.values.has(value)) return false;
  return true;
}

// ---------------------------------------------------------------------------

const NUMBER_WORDS: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
  hundred: 100,
  thousand: 1000,
  million: 1_000_000,
  dozen: 12,
};
const WORD = new RegExp(`\\b(${Object.keys(NUMBER_WORDS).join('|')})\\b`, 'gi');
const UUID = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const LIST_MARKER = /^\s*\d+[.)]\s+/gm;
const CODE = /\b([A-Za-z]{1,4})-?(\d+)\b/g;
const DIGITS = /\d+(?:,\d{3})*(?:\.\d+)?/g;

function numbersIn(text: string): { codes: Set<string>; values: Set<number> } {
  const codes = new Set<string>();
  const values = new Set<number>();
  const rest = text
    .replace(UUID, ' ')
    .replace(LIST_MARKER, ' ')
    .replace(CODE, (_match, letters: string, digits: string) => {
      codes.add(`${letters.toUpperCase()}${Number(digits)}`);
      return ' ';
    });
  for (const m of rest.matchAll(DIGITS)) values.add(Number(m[0].replace(/,/g, '')));
  for (const m of rest.matchAll(WORD)) values.add(NUMBER_WORDS[m[1]!.toLowerCase()]!);
  return { codes, values };
}

function collect(
  value: unknown,
  into: { codes: Set<string>; values: Set<number> },
  depth: number,
): void {
  if (depth > 20) return;
  if (typeof value === 'number') {
    if (Number.isFinite(value)) into.values.add(value);
  } else if (typeof value === 'string') {
    const found = numbersIn(value);
    found.codes.forEach((c) => into.codes.add(c));
    found.values.forEach((v) => into.values.add(v));
  } else if (Array.isArray(value)) {
    for (const item of value) collect(item, into, depth + 1);
  } else if (typeof value === 'object' && value !== null) {
    for (const item of Object.values(value)) collect(item, into, depth + 1);
  }
}

/** `E-010`, `e-10` and `E10` are one item. */
function codeKey(value: string): string {
  const m = /^\s*([A-Za-z]+)-?0*(\d+)\s*$/.exec(value);
  return m ? `${m[1]!.toUpperCase()}${m[2]}` : cleanText(value).toUpperCase();
}

function fold(value: string): string {
  return cleanText(value).toLowerCase();
}

function invalid(message: string): MapQueryResult {
  return { valid: false, check: 'invalid_query', message };
}
